<script lang="ts" setup>
import type { Category, CompositionOfWok } from '@/types/api/api-v3/make-wok'
import { Product } from '@/types/api/api-v3/make-wok'

import WokGeneratorImage from '@/components/Wok/WokGeneratorImage.vue'

const { isDesktop } = useResponsive()

type Props = {
  categories: Category[]
  compositionOfWok: CompositionOfWok
  totalPrice: number | undefined
  totalWeight: number
  currency: string | undefined
  selectedWok?: Product
}

const props = defineProps<Props>()

const filterWeight = computed(() =>
  props.totalWeight < 1000
    ? `${props.totalWeight} г`
    : `${(props.totalWeight / 1000).toFixed(2)} кг`,
)

const emit = defineEmits(['add-wok-to-basket', 'on-disabled-add'])

const onClickAddWok = () => {
  // Только если кликнули по задисебленной кнопке
  if (props.selectedWok) return

  emit('on-disabled-add')
}
</script>

<template>
  <div class="panel">
    <wok-generator-image
      v-if="isDesktop"
      class="panel__image"
      :composition-of-wok="compositionOfWok"
    />

    <div class="panel__information">
      <div class="panel__products">
        <span v-for="(category, index) in categories" :key="category.name">
          <span
            v-if="
              compositionOfWok[category.name] &&
              compositionOfWok[category.name].name
            "
            class="panel__product"
          >
            {{ compositionOfWok[category.name].name }}
            <span v-if="index !== categories.length - 1">
              {{ '+ ' }}
            </span>
          </span>
          <span v-else-if="category.name" class="panel__category">
            {{ category.name }}
            <span v-if="index !== categories.length - 1">
              {{ '+ ' }}
            </span>
          </span>
        </span>
      </div>

      <div class="panel__price-wrapper">
        <div v-if="totalPrice !== undefined" class="panel__price">
          {{ totalPrice }} {{ currency }}
        </div>

        <div v-if="totalWeight !== undefined" class="panel__weight">
          {{ filterWeight }}
        </div>
      </div>

      <div class="panel__button-container" @click="onClickAddWok">
        <the-button
          class="panel__button"
          type="button"
          size="sm"
          radius="lg"
          :disabled="!selectedWok"
          @click="emit('add-wok-to-basket')"
        >
          Хочу!
        </the-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.panel {
  &__image {
    @include media('>=desktop-mini') {
      width: 100%;
      margin-bottom: 40px;
      border-radius: 40px;
    }
  }

  &__information {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 10px;
    background: var(--ui-white);
    border-top: 1px solid var(--ui-grey);

    @include media('>=desktop-mini') {
      flex-wrap: wrap;
      padding: 0;
      border-top: unset;
    }
  }

  &__products {
    flex: 0 1 180px;
    margin-right: 10px;
    font-size: 14px;

    @include media('>=desktop-mini') {
      flex: 0 1 100%;
      margin-right: 0;
      margin-bottom: 20px;
      font-size: 24px;
    }
  }

  &__product {
    font-weight: 800;
  }

  &__category {
    color: var(--ui-unactive-gray);
    text-transform: lowercase;
  }

  &__price-wrapper {
    flex: 0 0 auto;
    margin-right: 10px;

    @include media('>=desktop-mini') {
      display: flex;
      align-items: center;
    }
  }

  &__price {
    font-size: 14px;
    font-weight: 800;

    @include media('>=desktop-mini') {
      order: 2;
      font-size: 24px;
      color: var(--ui-text-primary);
    }
  }

  &__weight {
    font-size: 14px;
    font-weight: 500;
    color: var(--ui-dark-grey);

    @include media('>=desktop-mini') {
      order: 1;
      margin-right: 16px;
      font-size: 14px;
    }
  }

  &__button {
    width: 90px;

    @include media('>=desktop-mini') {
      width: 120px;
    }
  }
}
</style>
